import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Container,
  Grid,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Paper,
  Divider,
  alpha,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

// Icons
import LoyaltyOutlinedIcon from "@mui/icons-material/LoyaltyOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import HomeLayout from "../../asset/layout/HomeLayout";
import {
  getFeaturedSection,
  getSectionMetadata,
  updateSectionMetadata,
  getFeaturedItems,
  createFeaturedItem,
  updateFeaturedItem,
  deleteFeaturedItem,
  updateFeaturedItemDisplayOrder,
  batchReorderFeaturedItems,
} from "../../actions/featuredSectionAuth";

const SECTION_KEY = "featured";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const getItemImage = (item) => {
  if (!item) return "";
  if (item.imageUrl) return item.imageUrl;
  if (item.image) {
    if (item.image.startsWith("http://") || item.image.startsWith("https://")) {
      return item.image;
    }
    return `https://backend.siripay.co${item.image.startsWith("/") ? "" : "/"}${item.image}`;
  }
  if (item.imagePath) {
    if (item.imagePath.startsWith("http://") || item.imagePath.startsWith("https://")) {
      return item.imagePath;
    }
    return `https://backend.siripay.co${item.imagePath.startsWith("/") ? "" : "/"}${item.imagePath}`;
  }
  return "";
};

const MetricCard = ({ label, value, sublabel, accent, icon: Icon, chip }) => (
  <Card
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      bgcolor: "#ffffff",
      border: "1px solid",
      borderColor: alpha(accent, 0.14),
      boxShadow: `0 4px 20px ${alpha(accent, 0.08)}`,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: -20,
        right: -20,
        width: 100,
        height: 100,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${alpha(accent, 0.18)} 0%, transparent 70%)`,
        pointerEvents: "none",
      }}
    />
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
      <Box
        sx={{
          p: 1.25,
          borderRadius: "14px",
          bgcolor: alpha(accent, 0.1),
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
      </Box>
      {chip && (
        <Chip
          size="small"
          label={chip}
          sx={{
            fontWeight: 600,
            fontSize: "0.72rem",
            bgcolor: alpha(accent, 0.08),
            color: accent,
            border: `1px solid ${alpha(accent, 0.2)}`,
          }}
        />
      )}
    </Stack>
    <Box>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", my: 0.5 }}>
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
          {sublabel}
        </Typography>
      )}
    </Box>
  </Card>
);

export default function FeaturedVoucher() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Section State
  const [sectionData, setSectionData] = useState({
    name: "Featured Voucher",
    title: "Featured Voucher",
    description: "Manage vouchers and promotional deals featured across SiriPay.",
    active: true,
  });
  const [sectionLoading, setSectionLoading] = useState(true);

  // Items State
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters & Controls
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, ACTIVE, INACTIVE
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "table"

  // Modals
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: "",
    title: "",
    link: "",
    image: "",
    displayOrder: 1,
    active: true,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Reorder Modal
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [reorderSubmitting, setReorderSubmitting] = useState(false);

  // Section Settings Modal
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    name: "Featured Voucher",
    description: "",
    active: true,
  });
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);

  // Delete Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auth Guard
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  // Fetch Section Metadata
  const fetchSectionInfo = useCallback(() => {
    setSectionLoading(true);
    dispatch(
      getSectionMetadata(
        SECTION_KEY,
        (res) => {
          const data = res?.data?.data ?? res?.data ?? res;
          if (data && typeof data === "object") {
            setSectionData({
              name: data.name || data.title || "Featured Voucher",
              title: data.title || data.name || "Featured Voucher",
              description: data.description || "",
              active: data.active !== false,
            });
            setSettingsForm({
              name: data.name || data.title || "Featured Voucher",
              description: data.description || "",
              active: data.active !== false,
            });
          }
          setSectionLoading(false);
        },
        (err) => {
          console.warn("Could not load section metadata:", err);
          setSectionLoading(false);
        }
      )
    );
  }, [dispatch]);

  // Fetch Items List
  const fetchItems = useCallback(() => {
    setItemsLoading(true);
    const params = {
      page,
      size: rowsPerPage,
      section: SECTION_KEY,
      search: debouncedSearch,
    };
    if (statusFilter === "ACTIVE") params.active = true;
    if (statusFilter === "INACTIVE") params.active = false;

    dispatch(
      getFeaturedItems(
        params,
        (res) => {
          const body = res?.data ?? res;
          const payload = body?.data ?? body;
          const content = Array.isArray(payload?.content)
            ? payload.content
            : Array.isArray(payload)
            ? payload
            : [];

          setItems(content);
          setTotalElements(payload?.totalElements ?? content.length);
          setTotalPages(payload?.totalPages ?? Math.ceil(content.length / rowsPerPage));
          setItemsLoading(false);
        },
        (err) => {
          console.error("Failed to load featured items:", err);
          // Fallback: try fetching full section with items
          dispatch(
            getFeaturedSection(
              SECTION_KEY,
              false,
              (fallbackRes) => {
                const fData = fallbackRes?.data?.data ?? fallbackRes?.data ?? fallbackRes;
                const fallbackItems = Array.isArray(fData?.items) ? fData.items : [];
                setItems(fallbackItems);
                setTotalElements(fallbackItems.length);
                setTotalPages(1);
                setItemsLoading(false);
              },
              () => {
                enqueueSnackbar("Failed to fetch featured items", { variant: "error" });
                setItemsLoading(false);
              }
            )
          );
        }
      )
    );
  }, [dispatch, page, rowsPerPage, debouncedSearch, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchSectionInfo();
  }, [fetchSectionInfo]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Compute Metrics
  const metrics = useMemo(() => {
    const total = totalElements || items.length;
    const activeCount = items.filter((i) => i.active !== false).length;
    const inactiveCount = items.filter((i) => i.active === false).length;
    const maxOrder = items.reduce(
      (max, i) => Math.max(max, Number(i.displayOrder || i.order || 0)),
      0
    );
    return { total, activeCount, inactiveCount, maxOrder };
  }, [items, totalElements]);

  // Open Add Item Modal
  const handleOpenAddModal = () => {
    setEditingItem(null);
    setItemForm({
      name: "",
      title: "Featured Voucher",
      link: "",
      image: "",
      displayOrder: metrics.maxOrder + 1,
      active: true,
    });
    setSelectedFile(null);
    setPreviewUrl("");
    setItemModalOpen(true);
  };

  // Open Edit Item Modal
  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || "",
      title: item.title || "",
      link: item.link || "",
      image: item.image || item.imagePath || item.imageUrl || "",
      displayOrder: item.displayOrder != null ? item.displayOrder : 1,
      active: item.active !== false,
    });
    setSelectedFile(null);
    setPreviewUrl(getItemImage(item));
    setItemModalOpen(true);
  };

  // Handle File Input
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(localUrl);
    }
  };

  // Save Item (Create or Update)
  const handleSubmitItem = (e) => {
    e.preventDefault();
    if (!itemForm.name.trim()) {
      enqueueSnackbar("Voucher / brand name is required", { variant: "warning" });
      return;
    }

    setFormSubmitting(true);
    const payload = {
      ...itemForm,
      sectionKey: SECTION_KEY,
    };

    if (editingItem?.id) {
      dispatch(
        updateFeaturedItem(
          editingItem.id,
          payload,
          selectedFile,
          () => {
            setFormSubmitting(false);
            setItemModalOpen(false);
            enqueueSnackbar("Featured voucher updated successfully!", { variant: "success" });
            fetchItems();
          },
          (err) => {
            setFormSubmitting(false);
            const msg = err?.response?.data?.message || "Failed to update item";
            enqueueSnackbar(msg, { variant: "error" });
          }
        )
      );
    } else {
      dispatch(
        createFeaturedItem(
          payload,
          selectedFile,
          () => {
            setFormSubmitting(false);
            setItemModalOpen(false);
            enqueueSnackbar("Featured voucher created successfully!", { variant: "success" });
            fetchItems();
          },
          (err) => {
            setFormSubmitting(false);
            const msg = err?.response?.data?.message || "Failed to create item";
            enqueueSnackbar(msg, { variant: "error" });
          }
        )
      );
    }
  };

  // Quick Toggle Item Active Status
  const handleToggleItemStatus = (item) => {
    const newStatus = !item.active;
    const payload = {
      name: item.name,
      title: item.title,
      link: item.link,
      image: item.image || item.imagePath || item.imageUrl,
      displayOrder: item.displayOrder,
      active: newStatus,
      sectionKey: SECTION_KEY,
    };

    dispatch(
      updateFeaturedItem(
        item.id,
        payload,
        null,
        () => {
          setItems((prev) =>
            prev.map((i) => (i.id === item.id ? { ...i, active: newStatus } : i))
          );
          enqueueSnackbar(
            `Voucher ${newStatus ? "activated" : "deactivated"} successfully`,
            { variant: "success" }
          );
        },
        (err) => {
          const msg = err?.response?.data?.message || "Failed to change status";
          enqueueSnackbar(msg, { variant: "error" });
        }
      )
    );
  };

  // Quick Update Item Display Order
  const handleUpdateItemOrder = (item, newOrder) => {
    const parsedOrder = parseInt(newOrder, 10);
    if (isNaN(parsedOrder) || parsedOrder < 1) return;

    dispatch(
      updateFeaturedItemDisplayOrder(
        item.id,
        parsedOrder,
        () => {
          setItems((prev) =>
            prev.map((i) => (i.id === item.id ? { ...i, displayOrder: parsedOrder } : i))
          );
          enqueueSnackbar("Display order updated", { variant: "success" });
        },
        (err) => {
          const msg = err?.response?.data?.message || "Failed to update order";
          enqueueSnackbar(msg, { variant: "error" });
        }
      )
    );
  };

  // Delete Item
  const handleConfirmDelete = () => {
    if (!itemToDelete?.id) return;
    setDeleteSubmitting(true);
    dispatch(
      deleteFeaturedItem(
        itemToDelete.id,
        () => {
          setDeleteSubmitting(false);
          setDeleteModalOpen(false);
          setItemToDelete(null);
          enqueueSnackbar("Featured voucher deleted successfully", { variant: "success" });
          fetchItems();
        },
        (err) => {
          setDeleteSubmitting(false);
          const msg = err?.response?.data?.message || "Failed to delete item";
          enqueueSnackbar(msg, { variant: "error" });
        }
      )
    );
  };

  // Open Batch Reorder Modal
  const handleOpenReorderModal = () => {
    // Sort items by current displayOrder
    const sorted = [...items].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
    setReorderList(sorted);
    setReorderModalOpen(true);
  };

  // Move item in reorder modal
  const handleMoveReorderItem = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= reorderList.length) return;
    const copy = [...reorderList];
    const [moved] = copy.splice(index, 1);
    copy.splice(targetIndex, 0, moved);
    setReorderList(copy);
  };

  // Save Batch Reorder
  const handleSaveBatchReorder = () => {
    setReorderSubmitting(true);
    const batchPayload = reorderList.map((item, idx) => ({
      id: item.id,
      displayOrder: idx + 1,
    }));

    dispatch(
      batchReorderFeaturedItems(
        batchPayload,
        () => {
          setReorderSubmitting(false);
          setReorderModalOpen(false);
          enqueueSnackbar("Items reordered successfully!", { variant: "success" });
          fetchItems();
        },
        (err) => {
          setReorderSubmitting(false);
          const msg = err?.response?.data?.message || "Failed to reorder items";
          enqueueSnackbar(msg, { variant: "error" });
        }
      )
    );
  };

  // Save Section Metadata Settings
  const handleSaveSettings = (e) => {
    e.preventDefault();
    setSettingsSubmitting(true);
    dispatch(
      updateSectionMetadata(
        SECTION_KEY,
        settingsForm,
        () => {
          setSettingsSubmitting(false);
          setSettingsModalOpen(false);
          setSectionData({ ...settingsForm, title: settingsForm.name });
          enqueueSnackbar("Section settings updated successfully!", { variant: "success" });
          fetchSectionInfo();
        },
        (err) => {
          setSettingsSubmitting(false);
          const msg = err?.response?.data?.message || "Failed to update section settings";
          enqueueSnackbar(msg, { variant: "error" });
        }
      )
    );
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          bgcolor: "#f8fafc",
          minHeight: "100vh",
          py: 4,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container maxWidth="xl" disableGutters>
          {/* Top Section Overview Card */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Card
              elevation={0}
              sx={{
                mb: 4,
                p: { xs: 3, md: 4 },
                borderRadius: "24px",
                background: "linear-gradient(135deg, #11409B 0%, #1e3a8a 60%, #0f172a 100%)",
                color: "#ffffff",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 12px 36px rgba(17, 64, 155, 0.22)",
              }}
            >
              {/* Background ambient accents */}
              <Box
                sx={{
                  position: "absolute",
                  top: -60,
                  right: -60,
                  width: 260,
                  height: 260,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(56,189,248,0.25) 0%, transparent 70%)",
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -40,
                  left: 200,
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(234,179,8,0.18) 0%, transparent 70%)",
                  pointerEvents: "none",
                }}
              />

              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={3}
                position="relative"
                zIndex={1}
              >
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "rgba(255,255,255,0.14)",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <LoyaltyOutlinedIcon sx={{ fontSize: 26, color: "#facc15" }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.85)",
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                      }}
                    >
                      FEATURED SECTION MANAGEMENT
                    </Typography>
                    <Chip
                      size="small"
                      label={sectionData.active ? "SECTION LIVE" : "SECTION INACTIVE"}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        bgcolor: sectionData.active
                          ? "rgba(34, 197, 94, 0.25)"
                          : "rgba(239, 68, 68, 0.25)",
                        color: sectionData.active ? "#86efac" : "#fca5a5",
                        border: `1px solid ${
                          sectionData.active ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)"
                        }`,
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      fontSize: { xs: "1.8rem", md: "2.4rem" },
                    }}
                  >
                    {sectionData.name || "Featured Voucher"}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1,
                      color: "rgba(255, 255, 255, 0.8)",
                      maxWidth: 680,
                      fontSize: "0.95rem",
                    }}
                  >
                    {sectionData.description ||
                      "Configure featured voucher cards and brand promotions spotlighted on the home screen."}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsOutlinedIcon />}
                    onClick={() => setSettingsModalOpen(true)}
                    sx={{
                      color: "#ffffff",
                      borderColor: "rgba(255, 255, 255, 0.35)",
                      borderRadius: "14px",
                      px: 2.5,
                      py: 1.25,
                      fontWeight: 600,
                      textTransform: "none",
                      backdropFilter: "blur(8px)",
                      bgcolor: "rgba(255, 255, 255, 0.08)",
                      "&:hover": {
                        borderColor: "#ffffff",
                        bgcolor: "rgba(255, 255, 255, 0.16)",
                      },
                    }}
                  >
                    Section Settings
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddModal}
                    sx={{
                      bgcolor: "#facc15",
                      color: "#0f172a",
                      borderRadius: "14px",
                      px: 3,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 6px 20px rgba(250, 204, 21, 0.35)",
                      "&:hover": {
                        bgcolor: "#eab308",
                      },
                    }}
                  >
                    Add Featured Voucher
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </motion.div>

          {/* Metric Cards Row */}
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="TOTAL FEATURED"
                value={metrics.total}
                sublabel="Vouchers configured in section"
                accent="#2563eb"
                icon={LoyaltyOutlinedIcon}
                chip="Catalog"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="ACTIVE VOUCHERS"
                value={metrics.activeCount}
                sublabel="Currently live to consumers"
                accent="#16a34a"
                icon={LoyaltyOutlinedIcon}
                chip="Live"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="INACTIVE / HIDDEN"
                value={metrics.inactiveCount}
                sublabel="Draft or paused vouchers"
                accent="#ea580c"
                icon={LoyaltyOutlinedIcon}
                chip="Draft"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="MAX SEQUENCE ORDER"
                value={`#${metrics.maxOrder}`}
                sublabel="Sequence priority on app"
                accent="#8b5cf6"
                icon={SwapVertOutlinedIcon}
                chip="Ordering"
              />
            </Grid>
          </Grid>

          {/* Controls Card: Search, Filter, Views, Actions */}
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "20px",
              bgcolor: "#ffffff",
              border: "1px solid",
              borderColor: alpha("#11409B", 0.08),
              boxShadow: "0 2px 16px rgba(15, 23, 42, 0.04)",
              mb: 3,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              {/* Left Search & Filter */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flex={1}>
                <TextField
                  placeholder="Search vouchers by name, title or link..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{
                    flex: 1,
                    maxWidth: { md: 380 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#f8fafc",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm("")}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value="ACTIVE">Active Only</MenuItem>
                    <MenuItem value="INACTIVE">Inactive Only</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {/* Right View toggle & Reorder button */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Tooltip title="Batch reorder vouchers sequence">
                  <Button
                    variant="outlined"
                    startIcon={<SwapVertOutlinedIcon />}
                    onClick={handleOpenReorderModal}
                    disabled={items.length === 0}
                    sx={{
                      borderRadius: "12px",
                      borderColor: alpha("#11409B", 0.25),
                      color: "#11409B",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#11409B",
                        bgcolor: alpha("#11409B", 0.04),
                      },
                    }}
                  >
                    Reorder Vouchers
                  </Button>
                </Tooltip>

                <Tooltip title="Refresh list">
                  <IconButton
                    onClick={fetchItems}
                    sx={{
                      border: "1px solid",
                      borderColor: alpha("#11409B", 0.15),
                      borderRadius: "12px",
                      color: "#11409B",
                    }}
                  >
                    <RefreshOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Grid / Table View Switcher */}
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 0.5,
                    borderRadius: "12px",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Tooltip title="Grid cards view">
                    <IconButton
                      size="small"
                      onClick={() => setViewMode("grid")}
                      sx={{
                        borderRadius: "8px",
                        bgcolor: viewMode === "grid" ? "#ffffff" : "transparent",
                        color: viewMode === "grid" ? "#11409B" : "#64748b",
                        boxShadow: viewMode === "grid" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      }}
                    >
                      <GridViewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Table list view">
                    <IconButton
                      size="small"
                      onClick={() => setViewMode("table")}
                      sx={{
                        borderRadius: "8px",
                        bgcolor: viewMode === "table" ? "#ffffff" : "transparent",
                        color: viewMode === "table" ? "#11409B" : "#64748b",
                        boxShadow: viewMode === "table" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      }}
                    >
                      <TableChartOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
            </Stack>
          </Card>

          {/* Items Content: Loading, Empty, Grid, or Table */}
          {itemsLoading ? (
            <Card
              elevation={0}
              sx={{
                p: 8,
                textAlign: "center",
                borderRadius: "20px",
                bgcolor: "#ffffff",
                border: "1px solid",
                borderColor: alpha("#11409B", 0.08),
              }}
            >
              <CircularProgress size={40} sx={{ color: "#11409B", mb: 2 }} />
              <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 600 }}>
                Loading featured vouchers...
              </Typography>
            </Card>
          ) : items.length === 0 ? (
            <Card
              elevation={0}
              sx={{
                p: 8,
                textAlign: "center",
                borderRadius: "20px",
                bgcolor: "#ffffff",
                border: "1px solid",
                borderColor: alpha("#11409B", 0.08),
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "50%",
                  bgcolor: alpha("#11409B", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#11409B",
                }}
              >
                <LoyaltyOutlinedIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                No Featured Vouchers Found
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, mb: 3 }}>
                {searchTerm || statusFilter !== "ALL"
                  ? "No vouchers matched your current search filters. Try clearing your search term."
                  : "You haven't added any featured vouchers to this section yet."}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddModal}
                sx={{
                  bgcolor: "#11409B",
                  color: "#fff",
                  borderRadius: "12px",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#0d3278" },
                }}
              >
                Add Your First Featured Voucher
              </Button>
            </Card>
          ) : viewMode === "grid" ? (
            /* Grid View */
            <Grid container spacing={2.5}>
              <AnimatePresence>
                {items.map((item, index) => {
                  const img = getItemImage(item);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item.id || index}>
                      <motion.div
                        custom={index}
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                        style={{ height: "100%" }}
                      >
                        <Card
                          elevation={0}
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            borderRadius: "20px",
                            bgcolor: "#ffffff",
                            border: "1px solid",
                            borderColor: alpha("#11409B", 0.1),
                            boxShadow: "0 2px 16px rgba(15, 23, 42, 0.04)",
                            transition: "transform 0.25s ease, box-shadow 0.25s ease",
                            overflow: "hidden",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "0 12px 32px rgba(17, 64, 155, 0.12)",
                              borderColor: alpha("#11409B", 0.25),
                            },
                          }}
                        >
                          {/* Card Image Banner */}
                          <Box
                            sx={{
                              height: 150,
                              bgcolor: "#f1f5f9",
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              p: 2,
                            }}
                          >
                            {img ? (
                              <Box
                                component="img"
                                src={img}
                                alt={item.name}
                                sx={{
                                  maxHeight: "100%",
                                  maxWidth: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <ImageOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
                            )}

                            {/* Order badge */}
                            <Chip
                              size="small"
                              label={`Order #${item.displayOrder ?? index + 1}`}
                              sx={{
                                position: "absolute",
                                top: 12,
                                left: 12,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                bgcolor: "rgba(15, 23, 42, 0.75)",
                                color: "#ffffff",
                                backdropFilter: "blur(6px)",
                              }}
                            />

                            {/* Active toggle badge */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                                borderRadius: "20px",
                                px: 1,
                                py: 0.25,
                                display: "flex",
                                alignItems: "center",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            >
                              <Switch
                                size="small"
                                checked={item.active !== false}
                                onChange={() => handleToggleItemStatus(item)}
                                color="success"
                              />
                            </Box>
                          </Box>

                          {/* Card Content */}
                          <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
                            {item.title && (
                              <Chip
                                size="small"
                                label={item.title}
                                sx={{
                                  alignSelf: "flex-start",
                                  mb: 1,
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                  bgcolor: alpha("#11409B", 0.08),
                                  color: "#11409B",
                                }}
                              />
                            )}
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {item.name || "Untitled Voucher"}
                            </Typography>

                            {item.link ? (
                              <Typography
                                component="a"
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{
                                  mt: 0.5,
                                  color: "#2563eb",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {item.link.length > 32
                                  ? `${item.link.substring(0, 32)}...`
                                  : item.link}
                                <OpenInNewOutlinedIcon sx={{ fontSize: 13 }} />
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ mt: 0.5, color: "#94a3b8" }}>
                                No target link
                              </Typography>
                            )}
                          </Box>

                          <Divider sx={{ borderColor: alpha("#11409B", 0.06) }} />

                          {/* Card Actions */}
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ p: 1.5, px: 2, bgcolor: "#fbfcfe" }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                                Order:
                              </Typography>
                              <Select
                                size="small"
                                value={item.displayOrder ?? index + 1}
                                onChange={(e) => handleUpdateItemOrder(item, e.target.value)}
                                sx={{
                                  height: 28,
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  borderRadius: "8px",
                                  "& .MuiSelect-select": { py: 0.5, px: 1 },
                                }}
                              >
                                {Array.from({ length: Math.max(items.length, 10) }, (_, i) => i + 1).map(
                                  (num) => (
                                    <MenuItem key={num} value={num}>
                                      #{num}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </Stack>

                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Edit voucher">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditModal(item)}
                                  sx={{
                                    color: "#2563eb",
                                    bgcolor: alpha("#2563eb", 0.08),
                                    "&:hover": { bgcolor: alpha("#2563eb", 0.16) },
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete voucher">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setItemToDelete(item);
                                    setDeleteModalOpen(true);
                                  }}
                                  sx={{
                                    color: "#dc2626",
                                    bgcolor: alpha("#dc2626", 0.08),
                                    "&:hover": { bgcolor: alpha("#dc2626", 0.16) },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </Card>
                      </motion.div>
                    </Grid>
                  );
                })}
              </AnimatePresence>
            </Grid>
          ) : (
            /* Table View */
            <Card
              elevation={0}
              sx={{
                borderRadius: "20px",
                bgcolor: "#ffffff",
                border: "1px solid",
                borderColor: alpha("#11409B", 0.08),
                boxShadow: "0 2px 16px rgba(15, 23, 42, 0.04)",
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>IMAGE</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>VOUCHER NAME</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>TITLE / BADGE</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569" }}>DESTINATION LINK</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", width: 120 }}>
                        DISPLAY ORDER
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", width: 120 }}>STATUS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#475569", width: 120 }}>
                        ACTIONS
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => {
                      const img = getItemImage(item);
                      return (
                        <TableRow
                          key={item.id || idx}
                          sx={{
                            "&:hover": { bgcolor: alpha("#11409B", 0.02) },
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                width: 56,
                                height: 42,
                                borderRadius: "10px",
                                bgcolor: "#f1f5f9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                p: 0.5,
                              }}
                            >
                              {img ? (
                                <Box
                                  component="img"
                                  src={img}
                                  alt={item.name}
                                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                />
                              ) : (
                                <ImageOutlinedIcon sx={{ color: "#94a3b8", fontSize: 24 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#0f172a" }}>
                            {item.name}
                          </TableCell>
                          <TableCell>
                            {item.title ? (
                              <Chip
                                size="small"
                                label={item.title}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                  bgcolor: alpha("#11409B", 0.08),
                                  color: "#11409B",
                                }}
                              />
                            ) : (
                              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.link ? (
                              <Typography
                                component="a"
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{
                                  color: "#2563eb",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {item.link.length > 36 ? `${item.link.substring(0, 36)}...` : item.link}
                                <OpenInNewOutlinedIcon sx={{ fontSize: 13 }} />
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              value={item.displayOrder ?? idx + 1}
                              onChange={(e) => handleUpdateItemOrder(item, e.target.value)}
                              sx={{
                                height: 32,
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                borderRadius: "8px",
                              }}
                            >
                              {Array.from({ length: Math.max(items.length, 10) }, (_, i) => i + 1).map(
                                (num) => (
                                  <MenuItem key={num} value={num}>
                                    #{num}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Switch
                                size="small"
                                checked={item.active !== false}
                                onChange={() => handleToggleItemStatus(item)}
                                color="success"
                              />
                              <Chip
                                size="small"
                                label={item.active !== false ? "Active" : "Inactive"}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  bgcolor:
                                    item.active !== false
                                      ? alpha("#16a34a", 0.1)
                                      : alpha("#dc2626", 0.1),
                                  color: item.active !== false ? "#16a34a" : "#dc2626",
                                }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditModal(item)}
                                sx={{
                                  color: "#2563eb",
                                  bgcolor: alpha("#2563eb", 0.08),
                                  "&:hover": { bgcolor: alpha("#2563eb", 0.16) },
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setItemToDelete(item);
                                  setDeleteModalOpen(true);
                                }}
                                sx={{
                                  color: "#dc2626",
                                  bgcolor: alpha("#dc2626", 0.08),
                                  "&:hover": { bgcolor: alpha("#dc2626", 0.16) },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {/* Pagination */}
          {items.length > 0 && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                Showing {items.length} of {totalElements} vouchers
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FormControl size="small">
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    sx={{ borderRadius: "10px", height: 32, fontSize: "0.8rem" }}
                  >
                    <MenuItem value={10}>10 per page</MenuItem>
                    <MenuItem value={20}>20 per page</MenuItem>
                    <MenuItem value={50}>50 per page</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
                >
                  Previous
                </Button>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#11409B" }}>
                  Page {page + 1} of {Math.max(totalPages, 1)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Container>

        {/* Modal 1: Add / Edit Item */}
        <Dialog
          open={itemModalOpen}
          onClose={() => !formSubmitting && setItemModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "24px", p: 1 },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
              {editingItem ? "Edit Featured Voucher" : "Add New Featured Voucher"}
            </Typography>
            <IconButton onClick={() => setItemModalOpen(false)} size="small" disabled={formSubmitting}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <form onSubmit={handleSubmitItem}>
            <DialogContent sx={{ pt: 1 }}>
              <Stack spacing={2.5}>
                <TextField
                  label="Voucher / Brand Name *"
                  fullWidth
                  placeholder="e.g. Amazon, Flipkart, Myntra"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                <TextField
                  label="Title / Badge Tag"
                  fullWidth
                  placeholder="e.g. Featured Voucher, Hot Deal, 10% Off"
                  value={itemForm.title}
                  onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                <TextField
                  label="Target Link (Redirect URL)"
                  fullWidth
                  placeholder="e.g. https://siripay.co/brands/amazon or /brands/amazon"
                  value={itemForm.link}
                  onChange={(e) => setItemForm({ ...itemForm, link: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                {/* Image Upload / URL Section */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", mb: 1, display: "block" }}>
                    VOUCHER CARD IMAGE
                  </Typography>

                  {/* Drag-and-drop or file selector */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: "16px",
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderColor: alpha("#11409B", 0.25),
                      textAlign: "center",
                      bgcolor: "#f8fafc",
                      cursor: "pointer",
                      "&:hover": { borderColor: "#11409B", bgcolor: alpha("#11409B", 0.02) },
                    }}
                    component="label"
                  >
                    <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                    <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "#11409B", mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Click or drag an image here to upload
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                      PNG, JPG, SVG, WebP up to 5MB
                    </Typography>
                  </Paper>

                  {/* Alternatively direct URL */}
                  <TextField
                    label="Or direct image URL"
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    placeholder="https://siripay.co/files/image.png"
                    value={itemForm.image}
                    onChange={(e) => {
                      setItemForm({ ...itemForm, image: e.target.value });
                      if (!selectedFile) setPreviewUrl(e.target.value);
                    }}
                  />

                  {/* Preview */}
                  {previewUrl && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: "14px",
                        bgcolor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Preview"
                        sx={{ width: 70, height: 48, objectFit: "contain", borderRadius: "8px", bgcolor: "#fff", p: 0.5 }}
                      />
                      <Box flex={1}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#0f172a" }}>
                          Image Preview
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                          {selectedFile ? selectedFile.name : itemForm.image || "Uploaded image"}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl("");
                          setItemForm({ ...itemForm, image: "" });
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Display Order"
                      type="number"
                      fullWidth
                      value={itemForm.displayOrder}
                      onChange={(e) => setItemForm({ ...itemForm, displayOrder: e.target.value })}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch
                        checked={itemForm.active}
                        onChange={(e) => setItemForm({ ...itemForm, active: e.target.checked })}
                        color="success"
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {itemForm.active ? "Active" : "Inactive"}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button
                variant="outlined"
                onClick={() => setItemModalOpen(false)}
                disabled={formSubmitting}
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={formSubmitting}
                sx={{
                  bgcolor: "#11409B",
                  color: "#fff",
                  borderRadius: "12px",
                  px: 3,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#0d3278" },
                }}
              >
                {formSubmitting ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : editingItem ? (
                  "Save Changes"
                ) : (
                  "Create Voucher"
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Modal 2: Batch Reorder */}
        <Dialog
          open={reorderModalOpen}
          onClose={() => !reorderSubmitting && setReorderModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                Reorder Featured Vouchers
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Use Up and Down arrows to adjust the order displayed to users.
              </Typography>
            </Box>
            <IconButton onClick={() => setReorderModalOpen(false)} size="small" disabled={reorderSubmitting}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1, maxHeight: "60vh" }}>
            <Stack spacing={1}>
              {reorderList.map((item, index) => {
                const img = getItemImage(item);
                return (
                  <Paper
                    key={item.id || index}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: "14px",
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
                      <Chip
                        size="small"
                        label={`#${index + 1}`}
                        sx={{ fontWeight: 800, bgcolor: "#11409B", color: "#fff", minWidth: 40 }}
                      />
                      <Box
                        sx={{
                          width: 44,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          p: 0.25,
                        }}
                      >
                        {img ? (
                          <Box component="img" src={img} alt={item.name} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        ) : (
                          <ImageOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                          {item.name}
                        </Typography>
                        {item.title && (
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {item.title}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        disabled={index === 0 || reorderSubmitting}
                        onClick={() => handleMoveReorderItem(index, -1)}
                        sx={{ color: "#11409B" }}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === reorderList.length - 1 || reorderSubmitting}
                        onClick={() => handleMoveReorderItem(index, 1)}
                        sx={{ color: "#11409B" }}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              variant="outlined"
              onClick={() => setReorderModalOpen(false)}
              disabled={reorderSubmitting}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveBatchReorder}
              disabled={reorderSubmitting}
              sx={{
                bgcolor: "#11409B",
                color: "#fff",
                borderRadius: "12px",
                px: 3,
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#0d3278" },
              }}
            >
              {reorderSubmitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Save New Order"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal 3: Section Settings */}
        <Dialog
          open={settingsModalOpen}
          onClose={() => !settingsSubmitting && setSettingsModalOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
              Section Settings
            </Typography>
            <IconButton onClick={() => setSettingsModalOpen(false)} size="small" disabled={settingsSubmitting}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <form onSubmit={handleSaveSettings}>
            <DialogContent sx={{ pt: 1 }}>
              <Stack spacing={2.5}>
                <TextField
                  label="Section Title / Name"
                  fullWidth
                  value={settingsForm.name}
                  onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
                  required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
                <TextField
                  label="Description / Subtitle"
                  fullWidth
                  multiline
                  rows={3}
                  value={settingsForm.description}
                  onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Section Active Status
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Toggle to display or hide the entire section on user apps
                    </Typography>
                  </Box>
                  <Switch
                    checked={settingsForm.active}
                    onChange={(e) => setSettingsForm({ ...settingsForm, active: e.target.checked })}
                    color="success"
                  />
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button
                variant="outlined"
                onClick={() => setSettingsModalOpen(false)}
                disabled={settingsSubmitting}
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={settingsSubmitting}
                sx={{
                  bgcolor: "#11409B",
                  color: "#fff",
                  borderRadius: "12px",
                  px: 3,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#0d3278" },
                }}
              >
                {settingsSubmitting ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Save Settings"
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Modal 4: Delete Confirmation */}
        <Dialog
          open={deleteModalOpen}
          onClose={() => !deleteSubmitting && setDeleteModalOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: "#0f172a" }}>
            Delete Featured Voucher?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Are you sure you want to remove <strong>{itemToDelete?.name || "this voucher"}</strong> from the featured section? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleteSubmitting}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteSubmitting}
              sx={{
                bgcolor: "#dc2626",
                color: "#fff",
                borderRadius: "12px",
                px: 3,
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#b91c1c" },
              }}
            >
              {deleteSubmitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </HomeLayout>
  );
}
